import React, { useEffect, useRef } from 'react'
import { subscribe } from 'react-contextual'
import { isEqual, isEmpty, get } from 'lodash-es'
import { InvoicesProvider } from '../../../../../providers'
import { Segment, CurrencyText } from '../../../../../components'
import Close from '../../../../atoms/Svgs/Close'
import styles from './Snapshot.module.scss'
import { LayoutContext } from 'modules/Layout/LayoutProvider'
import { $TSFixMe } from 'types/ts-migrate'
import { TAccount } from 'views/components/molecules/AccountSelector/AccountSelector'
import ActiveInvoicesProvider from './ActiveInvoicesProvider'
import ActiveInvoices from './ActiveInvoices'
import { splitCamelCaseIntoClass } from 'utils/string'
import { addHours, format } from 'date-fns'

interface SnapshotProps {
  redirect?: (...args: any[]) => any
  currentInvoice?: $TSFixMe
  fetchInvoiceActivity: (customerId: string, accountId: string) => void
  fetchInvoiceParty: (customerId: string) => void
  fetchPortfolio: (portfolioId: string) => void
  getCustomerSummary: (customerId: string, accountId: string) => $TSFixMe
  getCustomerRecentActivity: (customerId: string, accountId: string) => $TSFixMe[]
  getCustomerActiveInvoicesTotal: (customerId: string, accountId: string) => number
  getPartyById: (id: string) => $TSFixMe
  getPartyEmailById: (id: string) => string
  getPartyTelNumberById: (id: string) => string
  openContact: (id: string) => void
  secondaryPanel: $TSFixMe
}

const Snapshot = ({
  currentInvoice,
  fetchInvoiceActivity,
  fetchInvoiceParty,
  fetchPortfolio,
  getCustomerSummary,
  getCustomerRecentActivity,
  getCustomerActiveInvoicesTotal,
  getPartyById,
  getPartyEmailById,
  getPartyTelNumberById,
  openContact,
  secondaryPanel,
}: SnapshotProps): null | React.ReactElement => {
  const prevInvoiceRef = useRef<$TSFixMe>(undefined)

  useEffect(() => {
    const prevInvoice = prevInvoiceRef.current
    if (currentInvoice && !isEqual(prevInvoice, currentInvoice)) {
      fetchInvoiceActivity(currentInvoice.customerId, currentInvoice.accountId)
      fetchInvoiceParty(currentInvoice.customerId)
      // Ad hoc invoices have no lease, so portfolioId is undefined and the request would
      // be sent to /portfolios/undefined/entity.
      if (currentInvoice.portfolioId) {
        fetchPortfolio(currentInvoice.portfolioId)
      }
    }
    prevInvoiceRef.current = currentInvoice
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInvoice])

  const getSummary = (): $TSFixMe => getCustomerSummary(currentInvoice.customerId, currentInvoice.accountId)

  const getCustomerName = (): React.ReactElement => {
    const summary = getSummary()
    return <span>{!isEmpty(summary) ? summary.customerName : ''}</span>
  }

  const getAvailableFunds = (): React.ReactElement => {
    const summary = getSummary()
    return <CurrencyText>{!isEmpty(summary) ? summary.availableFunds : 0}</CurrencyText>
  }

  const getOpenInvoiceTotal = (): React.ReactElement => {
    return (
      <CurrencyText>
        {!currentInvoice ? 0 : getCustomerActiveInvoicesTotal(currentInvoice.customerId, currentInvoice.accountId)}
      </CurrencyText>
    )
  }

  const getClosingBalance = (): React.ReactElement => {
    return <CurrencyText>{getOpenInvoiceTotal().props.children - getAvailableFunds().props.children}</CurrencyText>
  }

  const invoice = currentInvoice

  if (!invoice) {
    return null
  }

  const customerEmail = getPartyEmailById(invoice.customerId)
  const customerTelNumber = getPartyTelNumberById(invoice.customerId)

  const mapAccountFields = (account: $TSFixMe): TAccount => ({
    id: account.accountId,
    value: account.accountId,
    label: account.propertyAddress ? account.propertyAddress : 'No attached lease',
    tag: account.tag,
    portfolioId: account?.portfolioId,
    paymentReference: account.paymentReference,
  })

  const currentParty = getPartyById(invoice.customerId)
  const accounts = Object.keys(currentParty).includes('company')
    ? get(currentParty, 'company.accounts', []).map(mapAccountFields)
    : get(currentParty, 'person.accounts', []).map(mapAccountFields)
  const account = accounts?.find((acc: TAccount) => acc.id === invoice.accountId)

  const customerPaymentReference = get(account, 'paymentReference', '')

  return (
    <div className={styles.root}>
      <header>
        <div className={styles.controls}>
          {customerPaymentReference && <span className={styles.paymentReference}>{customerPaymentReference}</span>}
          <button className={styles.close} onClick={secondaryPanel.close}>
            <Close />
          </button>
        </div>

        <div className={styles.customer}>
          <h2>
            <button onClick={() => openContact(invoice.customerId)}>{getCustomerName()}</button>
          </h2>
          <p>
            {customerEmail} <br />
            {/** @todo create formatPhone util */}
            {customerTelNumber && `${customerTelNumber as string}`}
          </p>
        </div>
      </header>

      <Segment.Group>
        {/** @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; hortizontal: true; }' i... Remove this comment to see the full error message */}
        <Segment hortizontal>
          <div className={styles['available-funds']}>
            <Segment.Label text="Available Funds" />
            <span className={styles.amount}>{getAvailableFunds()}</span>
          </div>
        </Segment>

        <Segment>
          <div className={styles['open-invoices']}>
            <header>
              <Segment.Label text="Active Invoices" />
              <span className={styles.amount}>{getOpenInvoiceTotal()}</span>
            </header>
            <ActiveInvoicesProvider customerId={invoice.customerId} accountId={invoice.accountId}>
              <ActiveInvoices />
            </ActiveInvoicesProvider>
          </div>
        </Segment>

        <Segment state={getClosingBalance().props.children <= 0 ? 'default' : 'danger'}>
          <div className={styles['closing-balance']}>
            <Segment.Label text="Closing Balance" />
            <span className={styles['amount']}>{getClosingBalance()}</span>
          </div>
        </Segment>
      </Segment.Group>

      <Segment.Group>
        <Segment>
          <React.Fragment>
            <Segment.Label text="Wallet Activity" />
            <ul className={styles['recent-activity']}>
              {getCustomerRecentActivity(invoice.customerId, invoice.accountId).map((ra: any) => (
                <li
                  key={ra.sourceId}
                  className={
                    ra.description === 'Payment Reversed'
                      ? styles['payment-reversed']
                      : styles[splitCamelCaseIntoClass(ra.sourceType)]
                  }
                >
                  <div className={styles.description}>
                    <strong>{ra.description === 'Funds received' ? 'Funds Received' : ra.description}</strong>
                    <time>
                      {ra.description === 'Funds received'
                        ? format(new Date(ra.date), 'yyyy-MM-dd')
                        : format(addHours(new Date(ra.date), 2), 'yyyy-MM-dd HH:mm')}
                    </time>
                  </div>
                  <span className={styles.amount}>
                    <CurrencyText>{ra.amount}</CurrencyText>
                  </span>
                </li>
              ))}
            </ul>
          </React.Fragment>
        </Segment>
      </Segment.Group>
    </div>
  )
}

export default subscribe(
  [InvoicesProvider, LayoutContext],
  (
    {
      currentInvoice,
      fetchInvoiceActivity,
      fetchInvoiceParty,
      fetchPortfolio,
      getCustomerSummary,
      getCustomerRecentActivity,
      getCustomerActiveInvoicesTotal,
      getPartyById,
      getPartyEmailById,
      getPartyTelNumberById,
      openContact,
    }: $TSFixMe,
    { secondaryPanel }: $TSFixMe,
  ) => ({
    currentInvoice,
    fetchInvoiceActivity,
    fetchInvoiceParty,
    fetchPortfolio,
    getCustomerSummary,
    getCustomerRecentActivity,
    getCustomerActiveInvoicesTotal,
    getPartyById,
    getPartyEmailById,
    getPartyTelNumberById,
    openContact,
    secondaryPanel,
  }),
)(Snapshot)
